import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  signal,
  inject,
  computed,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TypedFormControl } from '@principle-theorem/ng-shared';
import { IPractice } from '@principle-theorem/principle-core/interfaces';
import { Firestore, WithRef } from '@principle-theorem/shared';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'pr-allocation-settings',
  imports: [CommonModule, ReactiveFormsModule, MatSlideToggleModule],
  templateUrl: './allocation-settings.component.html',
  styleUrl: './allocation-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AllocationSettingsComponent {
  matSnackbar = inject(MatSnackBar);
  practice = input.required<WithRef<IPractice>>();
  hasMultiplePractices = input<boolean>();
  includeDepositsInAllocations = new TypedFormControl<boolean>(false);
  saving = signal(false);
  practiceName = computed(() => this.practice().name);

  constructor() {
    toObservable(this.practice)
      .pipe(takeUntilDestroyed())
      .subscribe((practice) => {
        this.includeDepositsInAllocations.patchValue(
          practice.settings?.accounting?.includeDepositsInAllocations ?? false,
          { emitEvent: false }
        );
      });
  }

  async onToggleChange(value: boolean): Promise<void> {
    this.saving.set(true);

    try {
      const practice = this.practice();
      const settings = {
        ...practice.settings,
        accounting: {
          ...practice.settings?.accounting,
          includeDepositsInAllocations: value,
        },
      };
      await Firestore.patchDoc(practice.ref, { settings });
      this.matSnackbar.open('Successfully saved allocation settings.');
    } catch (error) {
      this.matSnackbar.open('Failed to save allocation settings.');
      this.includeDepositsInAllocations.patchValue(!value, {
        emitEvent: false,
      });
      throw error;
    } finally {
      this.saving.set(false);
    }
  }
}
